/* eslint-disable prettier/prettier */
import { useState, useEffect, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Dashboard components
import MDBox from "components/MDBox";
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Routes & Context
import routes from "routes";
import { useMaterialUIController, setMiniSidenav, setOpenConfigurator } from "context";

// Auth
import { AuthProvider } from "./context/AuthContext";
import ProtectedRoute from "./context/ProtectedRoute"; // ← Ensure this path is correct

// Shared Clio / DynamoDB dataset, kept alive across route changes
import { ReportDataProvider } from "./store/ReportDataContext";

// Images
import brandWhite from "assets/images/law.svg";
import brandDark from "assets/images/law.svg";
import { clioTokenManager } from "apis/clio";
import { getToken } from "apis/api";

import SignIn from "layouts/authentication/sign-in";
import ConfirmPassword from "layouts/authentication/confirmPassword";
import ResetPassword from "layouts/authentication/reset-password/cover";


export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    openConfigurator,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    darkMode,
  } = controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname, search } = useLocation();
  const params = new URLSearchParams(search);
  const state = params.get("state");
  const code = params.get("code");
  useEffect(() => {
    const TeststateRetrieved = async () => {
      if (state === "retrieved") {
        if (clioTokenManager.isTokenExpired() || clioTokenManager.getToken() === null || clioTokenManager.getToken() === undefined) {
          const tokens = await getToken(code);
          const parsed = JSON.parse(tokens.body);

          if (parsed.status_code !== 200) {
            params.set("state", "error");
          } else {
            clioTokenManager.setToken(parsed.response.access_token, parsed.response.expires_in);
            params.set("state", "done");
          }

        }
      }
    }
    TeststateRetrieved();

  }, [state])



  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);

  // 🛡️ Apply route protection
  const getRoutes = (allRoutes) =>
    allRoutes.flatMap((route) => {
      if (route.collapse) return getRoutes(route.collapse);

      if (route.route) {
        const element = route.protected ? (
          <ProtectedRoute>{route.component}</ProtectedRoute>
        ) : (
          route.component
        );

        return <Route exact path={route.route} element={element} key={route.key} />;
      }

      return [];
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  return (
    <AuthProvider>
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="Celg Report"
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
            {configsButton}
          </>
        )}
        {layout === "vr" && <Configurator />}
        <ReportDataProvider>
          <Routes>
            {getRoutes(routes)}
            <Route path="/authentication/sign-in" element={<SignIn />} />
            <Route path="/authentication/confirmePassword" element={<ConfirmPassword />} />
            <Route path="/authentication/resetPassword" element={<ResetPassword />} />
            <Route path="*" element={<Navigate to="/dashboard" />} />
          </Routes>
        </ReportDataProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}
